import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, CreditCard } from "lucide-react";
import { COMPANY } from "@/lib/compliance";

/**
 * Post-submit success card shown by InquiryForm. Pulled out to keep the
 * parent file under 300 lines and so the card can be unit-tested
 * independently if we ever need to.
 */
export default function InquirySuccess({ onReset }) {
    return (
        <div
            data-testid="form-success-message"
            className="enc-reveal relative overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.4)] bg-[#0b0e13] p-8 md:p-10"
            role="status"
            aria-live="polite"
        >
            <div className="radial-gold absolute -inset-10 blur-2xl" />
            <div className="relative flex items-start gap-4">
                <div className="mt-1 rounded-full border border-brand-gold/60 bg-[#0d1117] p-3">
                    <CheckCircle2 className="h-6 w-6 text-brand-gold2" />
                </div>
                <div>
                    <h3 className="font-serif text-3xl text-brand-ivory">
                        Thank you.
                    </h3>
                    <p className="mt-3 text-base leading-relaxed text-brand-muted">
                        Your inquiry was sent to {COMPANY.shortName}. A
                        licensed agent will review your request.
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                        Do not send sensitive information by unencrypted
                        email or this general form — call us if you need a
                        secure channel.
                    </p>
                    <p className="mt-4 text-sm text-brand-muted">
                        Need to speak with us right away? Call{" "}
                        <a
                            href={`tel:${COMPANY.phoneTel}`}
                            className="text-brand-gold2 underline underline-offset-4 hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            {COMPANY.phone}
                        </a>
                        .
                    </p>
                    <Link
                        to="/pay-premium"
                        data-testid="form-success-pay-premium-cta"
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        <CreditCard
                            aria-hidden="true"
                            className="h-3.5 w-3.5"
                        />
                        Already a client? Pay your premium →
                    </Link>
                    <div className="gold-hairline my-6 w-32" />
                    <button
                        type="button"
                        onClick={onReset}
                        data-testid="form-reset-button"
                        className="text-xs uppercase tracking-[0.3em] text-brand-goldSoft transition-colors hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                    >
                        Submit another request →
                    </button>
                </div>
            </div>
        </div>
    );
}
